<!--
  NavDrawer.vue
  Desktop navigation drawer (side rail). Shows navigation links, admin tools,
  and user info. Uses rail mode by default (icons only), expandable via the
  hamburger button in the app bar.

  All state is shared via composables — no props or emits needed.
-->
<template>
  <v-navigation-drawer
    v-model="navDrawer"
    :rail="navRail"
    permanent
  >
    <v-list density="comfortable" nav>
      <!-- Main navigation: Podcasts, Latest, Playlist -->
      <v-tooltip text="Podcasts" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-podcast"
            title="Podcasts"
            @click="router.push({ name: 'list-podcasts' })"
          >
            <template v-if="podcastsCount" v-slot:append>
              <v-chip size="x-small" variant="tonal">{{ podcastsCount }}</v-chip>
            </template>
          </v-list-item>
        </template>
      </v-tooltip>
      <v-tooltip text="Latest" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-history"
            title="Latest"
            @click="router.push({ name: 'list-latest' })"
          >
            <template v-if="latestCount" v-slot:append>
              <v-chip size="x-small" variant="tonal">{{ latestCount }}</v-chip>
            </template>
          </v-list-item>
        </template>
      </v-tooltip>
      <v-tooltip text="Playlist" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-playlist-music-outline"
            title="Playlist"
            @click="router.push({ name: 'playlist' })"
          >
            <template v-if="playlistCount" v-slot:append>
              <v-chip size="x-small" variant="tonal">{{ playlistCount }}</v-chip>
            </template>
          </v-list-item>
        </template>
      </v-tooltip>


      <!-- Add URL -->
      <v-tooltip text="Add URL" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-link"
            title="Add URL"
            @click="addUrlDialog = true"
          ></v-list-item>
        </template>
      </v-tooltip>

      <!-- Search episodes: global episode search page -->
      <v-tooltip text="Search episodes" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-magnify"
            title="Search episodes"
            @click="router.push({ name: 'search' })"
          ></v-list-item>
        </template>
      </v-tooltip>

      <!-- Saved searches: named searches, each with a dynamic RSS feed -->
      <v-tooltip text="Saved searches" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-rss"
            title="Saved searches"
            @click="router.push({ name: 'saved-searches' })"
          ></v-list-item>
        </template>
      </v-tooltip>

      <!-- Upload a file or a text: sideload a local audio/video file, or make
           an episode from text read aloud (the page's "Text to speech" mode) -->
      <v-tooltip text="Upload a file or a text" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-file-upload-outline"
            title="Upload a file or a text"
            @click="router.push({ name: 'sideload' })"
          ></v-list-item>
        </template>
      </v-tooltip>

      <v-divider class="my-2"></v-divider>

      <!-- Play History: episodes the user has played -->
      <v-tooltip text="Play History" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-play-box-multiple-outline"
            title="Play History"
            @click="router.push({ name: 'history' })"
          ></v-list-item>
        </template>
      </v-tooltip>

      <!-- Status: job queue status (downloads, metadata, monitor checks, AI
           summaries). The endpoint is already per-user gated server-side
           (GET /queue/status scopes to the requesting user), but this page
           used to only be reachable via the admin-only submenu below, so a
           regular user had no way to reach their OWN status page at all —
           only admins could see it, and even then everyone's jobs looked
           identical because it's the same per-user-scoped view for admins
           too. Moved up here, available to everyone. Same icon as the old
           admin-submenu entry it replaces. -->
      <v-tooltip text="Status" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-clock"
            title="Status"
            @click="router.push({ name: 'status' })"
          ></v-list-item>
        </template>
      </v-tooltip>

      <!-- Settings -->
      <v-tooltip text="Settings" location="end" :disabled="!isRail">
        <template v-slot:activator="{ props: tp }">
          <v-list-item
            v-bind="tp"
            prepend-icon="mdi-account-cog"
            title="Settings"
            @click="router.push({ name: 'settings' })"
          ></v-list-item>
        </template>
      </v-tooltip>


      <!-- Admin submenu: collapsible group with tooltips for rail mode -->
      <v-list-group v-if="isAdmin" value="admin">
        <template v-slot:activator="{ props }">
          <v-tooltip text="Admin" location="end" :disabled="!isRail">
            <template v-slot:activator="{ props: tp }">
              <v-list-item v-bind="{ ...props, ...tp }" title="Admin" class="text-primary">
                <template v-slot:prepend><v-icon icon="mdi-shield-crown" color="primary"></v-icon></template>
              </v-list-item>
            </template>
          </v-tooltip>
        </template>

        <!-- Admin sub-items (icon only in primary color, text inherits default).
             "Status" (personal) used to live here too — moved up to the main
             nav list (visible to all users, not just admins) since the page
             was always per-user scoped, admin or not. This "Status — All
             Users" entry is the deliberately SEPARATE admin-only view
             (GET /queue/status/all-users) — different icon (multi-account
             vs. the plain clock above) so the two are easy to tell apart at
             a glance, especially in rail mode where only the icon shows. -->
        <v-tooltip text="Status — All Users" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item v-bind="tp" @click="router.push({ name: 'admin-status' })">
              <template v-slot:prepend><v-icon icon="mdi-account-multiple-outline" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">Status — All Users</v-list-item-title>
            </v-list-item>
          </template>
        </v-tooltip>
        <v-tooltip text="Storage" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item v-bind="tp" @click="storageDialog = true">
              <template v-slot:prepend><v-icon icon="mdi-harddisk" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">Storage</v-list-item-title>
            </v-list-item>
          </template>
        </v-tooltip>
        <v-tooltip text="Cleanup cache" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item v-bind="tp" @click="handleCleanup()">
              <template v-slot:prepend><v-icon icon="mdi-broom" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">Cleanup cache</v-list-item-title>
            </v-list-item>
          </template>
        </v-tooltip>
        <v-tooltip text="Clear cache" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item v-bind="tp" @click="handleClearCache">
              <template v-slot:prepend><v-icon icon="mdi-close-circle-outline" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">Clear cache</v-list-item-title>
            </v-list-item>
          </template>
        </v-tooltip>
        <v-tooltip text="Reboot proxy" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item v-bind="tp" @click="handleRebootProxy">
              <template v-slot:prepend><v-icon icon="mdi-lightning-bolt-circle" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">Reboot proxy</v-list-item-title>
            </v-list-item>
          </template>
        </v-tooltip>
        <v-tooltip text="Check all podcasts" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item v-bind="tp" @click="handleCheckPodcasts()">
              <template v-slot:prepend><v-icon icon="mdi-refresh" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">Check all podcasts</v-list-item-title>
            </v-list-item>
          </template>
        </v-tooltip>
        <v-tooltip text="User Management" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item v-bind="tp" @click="router.push({ name: 'admin-users' })">
              <template v-slot:prepend><v-icon icon="mdi-account-group" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">User Management</v-list-item-title>
            </v-list-item>
          </template>
        </v-tooltip>
        <!-- Server Settings: the admin-only half of what used to be one mixed
             /settings page — cache retention, check intervals, feed-download
             toggle, cache-cleanup pause, etc. Different icon from the
             all-users "Settings" entry above so the two are easy to tell
             apart at a glance, same reasoning as Status vs Status — All Users. -->
        <v-tooltip text="Settings" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item v-bind="tp" @click="router.push({ name: 'admin-settings' })">
              <template v-slot:prepend><v-icon icon="mdi-tune" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">Settings</v-list-item-title>
            </v-list-item>
          </template>
        </v-tooltip>
        <!-- Architecture documentation nav entry — commented out (not
             deleted), route/component ("admin-architecture") still exist.
        <v-tooltip text="Architecture Docs" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item v-bind="tp" @click="router.push({ name: 'admin-architecture' })">
              <template v-slot:prepend><v-icon icon="mdi-file-document-outline" color="primary"></v-icon></template>
              <v-list-item-title class="text-primary">Architecture</v-list-item-title>
            </v-list-item>
          </template>
        </v-tooltip>
        -->
      </v-list-group>
    </v-list>

    <!-- Logout button pinned to the bottom of the drawer -->
    <template v-slot:append>
      <v-divider></v-divider>
      <v-list density="comfortable" nav>
        <v-tooltip text="Logout" location="end" :disabled="!isRail">
          <template v-slot:activator="{ props: tp }">
            <v-list-item
              v-bind="tp"
              prepend-icon="mdi-logout"
              title="Logout"
              @click="handleLogout"
            ></v-list-item>
          </template>
        </v-tooltip>
      </v-list>
    </template>
  </v-navigation-drawer>

  <!-- Consolidated storage report dialog (server/proxy disk + R2 cache/saved) -->
  <storage-dialog v-model="storageDialog"></storage-dialog>
</template>

<script setup>
/**
 * NavDrawer.vue - Script Section
 *
 * Desktop navigation drawer. Uses composables for all shared state
 * and imports API functions directly for admin actions.
 */

import { computed, ref } from "vue";
import StorageDialog from "./StorageDialog.vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useLogin } from "@/composables/useLogin.js";
import { useAppMenu } from "@/composables/useAppMenu.js";
import { useConfirmDialog } from "@/composables/useConfirmDialog.js";
import {
  cleanup,
  clear,
  checkPodcasts,
  rebootProxy,
} from "@/api/index.js";
import { logout } from "@/services/logout.js";

const router = useRouter();
const { mdAndUp } = useDisplay();
const { isAdmin } = useLogin();
const { confirm } = useConfirmDialog();

// Shared state from useAppMenu composable
const {
  snackbar,
  addUrlDialog,
  navDrawer,
  navRail,
  podcastsCount,
  latestCount,
  playlistCount,
} = useAppMenu();

/** True when drawer is in rail mode on desktop (used to enable tooltips) */
const isRail = computed(() => mdAndUp.value && navRail.value);

// Controls the consolidated Storage report dialog (server/proxy disk + R2 usage)
const storageDialog = ref(false);

// ── Admin action handlers ───────────────────────────────────────────
// Disk-space and saved-stats are consolidated into the Storage dialog
// (StorageDialog.vue, opened via the "Storage" item).

/** handleCleanup - Triggers server-side cache cleanup (deletes expired files). */
const handleCleanup = async () => {
  try {
    const response = await cleanup();
    snackbar.value = { show: true, color: "info", message: response.message };
  } catch (error) {
    snackbar.value = { show: true, color: "error", message: "Error cleaning up cache" };
  }
};

/** handleCheckPodcasts - Triggers a bulk check of all podcasts for new episodes.
 *  Shows immediate feedback since the server check can take several minutes. */
const handleCheckPodcasts = async () => {
  snackbar.value = { show: true, color: "info", message: "Checking all podcasts..." };
  try {
    const response = await checkPodcasts();
    snackbar.value = { show: true, color: "success", message: response.message };
  } catch (error) {
    snackbar.value = { show: true, color: "error", message: "Error checking podcasts" };
  }
};

/** handleClearCache - Confirms before clearing all cached files. */
const handleClearCache = async () => {
  const ok = await confirm({
    title: "Confirm clearing the cache?",
    text: "This will delete all files in the cache",
  });
  if (!ok) return;
  try {
    const response = await clear();
    snackbar.value = { show: true, color: "info", message: response.message };
  } catch (error) {
    snackbar.value = { show: true, color: "error", message: "Error clearing cache" };
  }
};

/** handleRebootProxy - Confirms before rebooting the remote proxy server. */
const handleRebootProxy = async () => {
  const ok = await confirm({
    title: "Confirm proxy reboot?",
    text: "This will reboot the remote proxy server used to download the episode files (audio, video)",
  });
  if (!ok) return;
  try {
    const response = await rebootProxy();
    snackbar.value = { show: true, color: "info", message: response.message };
  } catch (error) {
    snackbar.value = { show: true, color: "error", message: "Error rebooting proxy server" };
  }
};

/** handleLogout - Confirms before logging out. */
const handleLogout = async () => {
  const ok = await confirm({
    title: "Confirm Logout",
    text: "Are you sure you want to logout FYT?",
  });
  if (ok) {
    logout();
  }
};
</script>

<style scoped>
/* Remove default indentation from admin list group children */
/* Remove default indentation from admin list group children so they align with top-level items */
:deep(.v-list-group__items .v-list-item) {
  padding-inline-start: 8px !important;
}
</style>
